import {
  generateSftpGroupPath,
  getSftpFieldsError,
  SFTP_NOT_FOR_INTERNAL_MESSAGE,
  STEERFI_PARTY_ID,
} from '@steerfi/shared';
import { IconLoader2, IconUserPlus } from '@tabler/icons-react';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { type ClientData, getClients } from '@/api/clients';
import { type CreateUserRequest, createUser, type RealmRole } from '@/api/users';
import { SftpAccessFields } from '@/components/sftp-access-fields';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { accessLevelFromScopeTier, rolesForAccessLevel } from '@/lib/authz';
import { formatErrorMessage } from '@/utils/error-formatting';

interface CreateUserModalProps {
  onSuccess?: () => void;
}

const initialFormData: CreateUserRequest = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  password: '',
  partyId: '',
  roles: ['basic'],
  isSftp: false,
  sftpUsername: '',
  sshPublicKeys: [],
};

function FormRow({
  label,
  htmlFor,
  required,
  alignTop,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  alignTop?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`grid grid-cols-4 gap-4 ${alignTop ? 'items-start' : 'items-center'}`}>
      <Label htmlFor={htmlFor} className={`text-right ${alignTop ? 'pt-2' : ''}`}>
        {label}
        {required && ' *'}
      </Label>
      <div className="col-span-3">{children}</div>
    </div>
  );
}

export const CreateUserModal: FC<CreateUserModalProps> = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateUserRequest>(initialFormData);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  // Access level is chosen as two orthogonal controls — scope (internal vs external)
  // and tier (basic vs admin) — translated to realm roles on submit.
  const [isInternalScope, setIsInternalScope] = useState(false);
  const [tier, setTier] = useState<'basic' | 'admin'>('basic');

  const loadClients = useCallback(async () => {
    try {
      setClientsLoading(true);
      const response = await getClients({ limit: 100, sortBy: 'name', order: 'asc' });
      setClients(response.data);
    } catch {
      // Silently fail - user will see empty dropdown
    } finally {
      setClientsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadClients();
    }
  }, [open, loadClients]);

  const updateFormData = (
    key: keyof CreateUserRequest,
    value: string | boolean | string[] | RealmRole[]
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // SFTP access is scoped to a client, since the Keycloak group path is derived
  // from the client name. With no client the path is blank, which the SFTP fields
  // surface and submit rejects.
  //
  // Derived from the *effective* party: internal users are assigned to SteerFi
  // regardless of any client picked earlier, so switching to internal scope must
  // drop the group rather than leave a stale client path behind.
  const effectivePartyId = isInternalScope ? '' : formData.partyId;
  const selectedClient = clients.find((c) => c.id === effectivePartyId);
  const sftpGroupPath = selectedClient ? generateSftpGroupPath(selectedClient.name) : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Basic client-side validation
      if (!formData.firstName.trim()) {
        throw new Error('First name is required');
      }
      if (!formData.lastName.trim()) {
        throw new Error('Last name is required');
      }
      if (!formData.email.trim()) {
        throw new Error('Email is required');
      }
      if (!formData.password || formData.password.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }
      if (!isInternalScope && !formData.partyId) {
        throw new Error('Please select a client');
      }

      const enableSftp = formData.isSftp === true;
      const sshPublicKeys = formData.sshPublicKeys ?? [];

      if (enableSftp) {
        // Reachable if SFTP was switched on before internal scope was selected.
        if (isInternalScope) {
          throw new Error(SFTP_NOT_FOR_INTERNAL_MESSAGE);
        }
        if (!sftpGroupPath) {
          throw new Error('Select a client to generate the SFTP group path');
        }
        // Same rules the API enforces, so the form fails fast.
        const sftpError = getSftpFieldsError({
          sftpUsername: formData.sftpUsername,
          sshPublicKeys,
        });
        if (sftpError) throw new Error(sftpError);
      }

      const roles = rolesForAccessLevel(
        accessLevelFromScopeTier(isInternalScope, tier === 'admin')
      ) as RealmRole[];
      // Internal users belong to the SteerFi internal party, not a specific client.
      const partyId = isInternalScope ? STEERFI_PARTY_ID : formData.partyId;
      const trimmedPhone = formData.phoneNumber?.trim();
      const payload: CreateUserRequest = {
        ...formData,
        partyId,
        roles,
        ...(trimmedPhone ? { phoneNumber: trimmedPhone } : {}),
        // Only send SFTP fields when it is actually being enabled, so a
        // non-SFTP user is not rejected for carrying keys or a group.
        ...(enableSftp
          ? {
              isSftp: true,
              keycloakGroup: sftpGroupPath,
              sftpUsername: (formData.sftpUsername ?? '').trim() || null,
              sshPublicKeys,
            }
          : { isSftp: false, sftpUsername: null, sshPublicKeys: [] }),
      };

      await createUser(payload);

      setOpen(false);
      setFormData(initialFormData);
      setIsInternalScope(false);
      setTier('basic');
      onSuccess?.();
    } catch (err) {
      const errorMessage = formatErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setIsInternalScope(false);
    setTier('basic');
    setError(null);
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setFormData(initialFormData);
      setIsInternalScope(false);
      setTier('basic');
      setError(null);
    }
    setOpen(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="px-3">
          <IconUserPlus className="mr-2 h-4 w-4" />
          New User
        </Button>
      </DialogTrigger>
      {/* Capped height with the body as the only scroll region, so the header,
          the error banner, and the footer buttons stay reachable on short
          viewports — the form is tall once SFTP access is expanded. */}
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] !flex !flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            Create a new user and assign them to a client with specific permissions. Fields marked
            with * are required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md shrink-0">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="grid gap-4 py-4 flex-1 min-h-0 overflow-y-auto">
            <FormRow label="First Name" htmlFor="firstName" required>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => updateFormData('firstName', e.target.value)}
                placeholder="Enter first name"
                required
              />
            </FormRow>

            <FormRow label="Last Name" htmlFor="lastName" required>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => updateFormData('lastName', e.target.value)}
                placeholder="Enter last name"
                required
              />
            </FormRow>

            <FormRow label="Email" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
                placeholder="user@example.com"
                required
              />
            </FormRow>

            <FormRow label="Phone" htmlFor="phoneNumber">
              <Input
                id="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => updateFormData('phoneNumber', e.target.value)}
                placeholder="Optional"
              />
            </FormRow>

            <FormRow label="Password" htmlFor="password" required>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => updateFormData('password', e.target.value)}
                placeholder="Minimum 8 characters"
                minLength={8}
                required
              />
            </FormRow>

            <FormRow label="Client" htmlFor="partyId" required={!isInternalScope}>
              <Select
                value={isInternalScope ? '' : formData.partyId}
                onValueChange={(value) => updateFormData('partyId', value)}
                disabled={isInternalScope}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      isInternalScope
                        ? 'SteerFi (internal user)'
                        : clientsLoading
                          ? 'Loading clients...'
                          : 'Select a client'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormRow>

            <FormRow label="Scope" alignTop>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="scope-internal"
                  checked={isInternalScope}
                  onCheckedChange={(checked) => setIsInternalScope(checked === true)}
                  disabled={isLoading}
                />
                <Label htmlFor="scope-internal" className="font-normal cursor-pointer">
                  <span className="font-medium">SteerFi internal user</span>
                  <span className="text-muted-foreground ml-1">— cross-client internal access</span>
                </Label>
              </div>
            </FormRow>

            <FormRow label="Role" htmlFor="tier" required>
              <Select value={tier} onValueChange={(value) => setTier(value as 'basic' | 'admin')}>
                <SelectTrigger id="tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>

            <div className="border-t pt-4 mt-2">
              <FormRow label="SFTP Access" alignTop>
                <SftpAccessFields
                  idPrefix="create"
                  isSftp={formData.isSftp === true}
                  onIsSftpChange={(value) => updateFormData('isSftp', value)}
                  sftpUsername={formData.sftpUsername ?? ''}
                  onSftpUsernameChange={(value) => updateFormData('sftpUsername', value)}
                  sshPublicKeys={formData.sshPublicKeys ?? []}
                  onSshPublicKeysChange={(keys) => updateFormData('sshPublicKeys', keys)}
                  email={formData.email}
                  groupPath={sftpGroupPath}
                  enableNotice="SFTP users are created without MFA or email verification. This user will not be able to log into the web app without enrolling in MFA."
                  disabled={isLoading}
                  ineligibleReason={isInternalScope ? SFTP_NOT_FOR_INTERNAL_MESSAGE : null}
                />
              </FormRow>
            </div>
          </div>

          <DialogFooter className="shrink-0 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
